import { StellarWalletsKit, parseError } from '@creit.tech/stellar-wallets-kit/sdk';
import { activeModule } from '@creit.tech/stellar-wallets-kit/state';
import { defaultModules } from '@creit.tech/stellar-wallets-kit/modules/utils';
import { Networks } from '@creit.tech/stellar-wallets-kit/types';
import { FREIGHTER_ID } from '@creit.tech/stellar-wallets-kit/modules/freighter';
import { HANA_ID } from '@creit.tech/stellar-wallets-kit/modules/hana';
import {
    getNetwork as freighterGetNetwork,
    signAuthEntry as freighterSignAuthEntry,
    signMessage as freighterSignMessage,
    signTransaction as freighterSignTransaction,
} from '@stellar/freighter-api';
import { CHAINS } from '../config/chains';
import { XBULL_ID } from '@creit.tech/stellar-wallets-kit/modules/xbull';
import { ALBEDO_ID } from '@creit.tech/stellar-wallets-kit/modules/albedo';
import { RABET_ID } from '@creit.tech/stellar-wallets-kit/modules/rabet';
import { LOBSTR_ID } from '@creit.tech/stellar-wallets-kit/modules/lobstr';

/** When `CHAINS.stellar.walletKitFullCatalog` is false, only these kit modules are loaded and offered in the UI. */
const STELLAR_KIT_MINIMAL_WALLET_IDS = new Set([FREIGHTER_ID, HANA_ID, XBULL_ID, LOBSTR_ID]);

function stellarKitUsesFullWalletCatalog() {
    return CHAINS.stellar?.walletKitFullCatalog === true;
}

function filterKitModulesByCatalogPreference(modules) {
    if (stellarKitUsesFullWalletCatalog()) return modules;
    return modules.filter((m) => STELLAR_KIT_MINIMAL_WALLET_IDS.has(m.productId));
}

/** Kit modules that reject SEP-43 `signAuthEntry` (Soroban invoker auth cannot be completed). */
const WALLETS_WITHOUT_SIGN_AUTH_ENTRY = new Set([
    LOBSTR_ID,
    XBULL_ID,
    RABET_ID,
    ALBEDO_ID,
    'LEDGER',
    'TREZOR',
    'wallet_connect',
    'BitgetWallet',
]);

/** Kit modules that reject SEP-43 `signMessage` (deterministic ElGamal key derivation cannot run). */
const WALLETS_WITHOUT_SIGN_MESSAGE = new Set([
    RABET_ID,
    ALBEDO_ID,
    'LEDGER',
    'TREZOR',
    'wallet_connect',
]);

const KIT_SELECTED_WALLET_KEY = '@creit.tech/stellar-wallets-kit/selectedWalletId';

/**
 * Theme for the kit's built-in auth/profile modal. Colors are pulled from the
 * app's palette in `App.css` (white surface, black text, light-blue accent)
 * so the modal feels native rather than the generic default.
 */
const KIT_APP_THEME = {
    background: '#ffffff',
    'background-secondary': '#f8fafc',
    'foreground-strong': '#0a0a0a',
    foreground: '#111827',
    'foreground-secondary': '#4b5563',
    primary: '#00A3FF',
    'primary-foreground': '#ffffff',
    transparent: 'rgba(0, 0, 0, 0)',
    lighter: '#f3f4f6',
    light: '#e5e7eb',
    'light-gray': '#d1d5db',
    gray: '#6b7280',
    danger: '#dc2626',
    border: '#e5e7eb',
    shadow: '0 20px 45px -12px rgba(15, 23, 42, 0.25)',
    'border-radius': '14px',
    'font-family':
        '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
};

let initialized = false;

/** Canonical passphrase for RPC / signing (honours `REACT_APP_STELLAR_NETWORK_PASSPHRASE`). */
export function getAppStellarNetworkPassphrase() {
    return String(CHAINS.stellar.networkPassphrase || '').trim();
}

/**
 * Freighter-style wallet network id (Hana uses the same shape in signing options).
 * Only defined for passphrases that match known Stellar networks.
 */
export function walletNetworkEnumFromPassphrase(networkPassphrase) {
    if (!networkPassphrase || typeof networkPassphrase !== 'string') return undefined;
    const p = networkPassphrase.trim();
    if (p === Networks.TESTNET) return 'TESTNET';
    if (p === Networks.PUBLIC) return 'PUBLIC';
    if (p === Networks.FUTURENET) return 'FUTURENET';
    if (p === Networks.SANDBOX) return 'SANDBOX';
    if (p === Networks.STANDALONE) return 'STANDALONE';
    return undefined;
}

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function hanaTopLevelWalletRequest(networkPassphrase, walletNetworkEnum) {
    const root = typeof window !== 'undefined' ? window.hanaWallet : null;
    if (!root || typeof root.request !== 'function') return;
    const methods = [
        'wallet_switchStellarNetwork',
        'stellar_switchNetwork',
        'switchStellarNetwork',
        'hana_switchStellarNetwork',
        'wallet_addStellarNetwork',
    ];
    for (const method of methods) {
        const paramVariants = [];
        if (walletNetworkEnum) {
            const nwLower = String(walletNetworkEnum).toLowerCase();
            paramVariants.push(
                { network: walletNetworkEnum },
                { network: nwLower },
                { network: walletNetworkEnum, networkPassphrase },
                { chainId: `stellar:${nwLower}` }
            );
        }
        paramVariants.push({ networkPassphrase }, { networkPassphrase, network: walletNetworkEnum || undefined });
        for (const params of paramVariants) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await root.request({ method, params });
                return;
            } catch (_) {
                /* try next */
            }
        }
    }
}

/**
 * Hana rejects signing when the extension’s active network does not match the request.
 * Pass Freighter-style `network: "TESTNET"` alongside `networkPassphrase` on every sign call,
 * and try switch helpers (with enum + passphrase) before signing.
 */
async function requestHanaStellarActiveNetwork(networkPassphrase, walletNetworkEnum) {
    const stellar = typeof window !== 'undefined' ? window.hanaWallet?.stellar : null;
    if (!stellar || typeof networkPassphrase !== 'string' || !networkPassphrase) return;

    const argVariants = [];
    if (walletNetworkEnum) {
        const nwLower = String(walletNetworkEnum).toLowerCase();
        // Prefer object-shaped calls first: some Hana builds accept unknown string args without
        // throwing (no-op), which would incorrectly short-circuit the whole probe.
        argVariants.push(
            { network: walletNetworkEnum, networkPassphrase },
            { network: nwLower, networkPassphrase },
            { network: walletNetworkEnum },
            { network: nwLower },
            { walletNetwork: walletNetworkEnum, networkPassphrase },
            { stellarNetwork: walletNetworkEnum, networkPassphrase },
            { networkName: walletNetworkEnum, networkPassphrase },
            walletNetworkEnum,
            nwLower
        );
    }
    argVariants.push({ networkPassphrase }, networkPassphrase, { passphrase: networkPassphrase });

    const directNames = [
        'switchNetwork',
        'requestSwitchNetwork',
        'setNetwork',
        'changeNetwork',
        'switchStellarNetwork',
        'selectNetwork',
        'setActiveNetwork',
        'activateNetwork',
        'updateNetwork',
    ];

    for (const name of directNames) {
        const fn = stellar[name];
        if (typeof fn !== 'function') continue;
        for (const arg of argVariants) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await fn.call(stellar, arg);
                return;
            } catch (_) {
                /* try next signature */
            }
        }
    }

    if (typeof stellar.request === 'function') {
        const methods = [
            'wallet_switchStellarNetwork',
            'stellar_switchNetwork',
            'switchStellarNetwork',
            'hana_switchStellarNetwork',
            'wallet_addStellarNetwork',
        ];
        for (const method of methods) {
            const paramList = [];
            if (walletNetworkEnum) {
                paramList.push(
                    { network: walletNetworkEnum },
                    { network: walletNetworkEnum, networkPassphrase },
                    { networkPassphrase }
                );
            } else {
                paramList.push({ networkPassphrase }, [networkPassphrase]);
            }
            for (const params of paramList) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    await stellar.request({ method, params });
                    return;
                } catch (_) {
                    /* try next */
                }
            }
        }
    }

    await hanaTopLevelWalletRequest(networkPassphrase, walletNetworkEnum);
}

/**
 * Keep kit `selectedNetwork` aligned with the app chain config and ask Hana (if active)
 * to use the same network before any signing RPC runs.
 */
/** Extra network fields some Hana builds read on signing RPCs (Freighter-compatible). */
function hanaSigningNetworkFields(networkPassphrase) {
    const network = walletNetworkEnumFromPassphrase(networkPassphrase);
    if (!network) return {};
    const nwLower = String(network).toLowerCase();
    return {
        network,
        walletNetwork: network,
        networkName: network,
        stellarNetwork: network,
        chainId: `stellar:${nwLower}`,
    };
}

/**
 * Freighter's extension messaging accepts `network` (e.g. TESTNET) alongside
 * `networkPassphrase` so signing matches the dApp even when the in-wallet
 * selector is easy to misread. The kit's Freighter module only forwarded the
 * passphrase, which breaks some Freighter builds for Soroban/signMessage.
 */
function freighterSigningOpts(networkPassphrase, address) {
    const opts = { networkPassphrase, address };
    const network = walletNetworkEnumFromPassphrase(networkPassphrase);
    if (network) opts.network = network;
    return opts;
}

export async function alignStellarWalletWithAppNetwork(networkPassphrase = getAppStellarNetworkPassphrase()) {
    ensureKitInitialized();
    StellarWalletsKit.setNetwork(networkPassphrase);
    if (activeModule.value?.productId === HANA_ID) {
        const walletNet = walletNetworkEnumFromPassphrase(networkPassphrase);
        // Always attempt alignment: Hana’s getNetwork* can report testnet while the signing UI
        // still treats the profile as mainnet, and undocumented switch helpers are no-ops until
        // the matching call shape is found.
        await requestHanaStellarActiveNetwork(networkPassphrase, walletNet);
        await delay(200);
    }
    if (activeModule.value?.productId === FREIGHTER_ID && typeof window !== 'undefined') {
        try {
            const { networkPassphrase: fp, error } = await freighterGetNetwork();
            if (!error) {
                const want = String(networkPassphrase || '').trim();
                const have = String(fp || '').trim();
                if (want && have && want !== have) {
                    console.warn(
                        '[StellarWalletsKit] Freighter is on a different network than this app. ' +
                            'Open Freighter → Settings / network and switch to Stellar testnet (same passphrase as the app), then retry.'
                    );
                }
            }
        } catch (_) {
            /* ignore */
        }
    }
}

/**
 * Lazily initialize the Stellar Wallets Kit. Safe to call multiple times.
 */
export function ensureKitInitialized() {
    if (initialized) return;

    const persistedRaw =
        typeof window !== 'undefined' && window.localStorage
            ? window.localStorage.getItem(KIT_SELECTED_WALLET_KEY) || undefined
            : undefined;
    const persisted =
        persistedRaw &&
        (stellarKitUsesFullWalletCatalog() || STELLAR_KIT_MINIMAL_WALLET_IDS.has(persistedRaw))
            ? persistedRaw
            : undefined;

    const network = getAppStellarNetworkPassphrase() || Networks.TESTNET;

    StellarWalletsKit.init({
        modules: filterKitModulesByCatalogPreference(defaultModules()),
        network,
        selectedWalletId: persisted,
        theme: KIT_APP_THEME,
        authModal: {
            showInstallLabel: true,
            hideUnsupportedWallets: false,
        },
    });

    initialized = true;
}

/**
 * Map a Stellar Wallets Kit module product id to the internal origin string
 * the app has used historically. Any non-freighter/non-hana wallet is mapped
 * to the generic `'stellar'` origin so all signing paths still flow through
 * the kit.
 */
export function kitWalletIdToAppOrigin(walletId) {
    if (!walletId) return null;
    switch (walletId) {
        case FREIGHTER_ID:
            return 'freighter';
        case HANA_ID:
            return 'hana';
        case XBULL_ID:
            return 'xbull';
        case LOBSTR_ID:
            return 'lobstr';
        case ALBEDO_ID:
            return 'albedo';
        case RABET_ID:
            return 'rabet';
        default:
            return 'stellar';
    }
}

/**
 * Open the kit's built-in auth modal so the user can pick a Stellar wallet.
 * Returns `{ address, origin, walletId }` on success.
 */
export async function openStellarWalletsAuthModal() {
    ensureKitInitialized();
    const { address } = await StellarWalletsKit.authModal();
    const walletId = StellarWalletsKit.selectedModule?.productId || null;
    await alignStellarWalletWithAppNetwork();
    return {
        address,
        walletId,
        origin: kitWalletIdToAppOrigin(walletId),
    };
}

/**
 * Return the kit's catalog of supported Stellar wallets. We filter out
 * `BRIDGE_WALLET`s (WalletConnect-backed) because those need a WalletConnect
 * project ID in kit init, which we don't configure yet.
 */
export async function listSupportedStellarWallets() {
    ensureKitInitialized();
    try {
        const all = await StellarWalletsKit.refreshSupportedWallets();
        let list = (all || []).filter((w) => w && w.type !== 'BRIDGE_WALLET');
        if (!stellarKitUsesFullWalletCatalog()) {
            list = list.filter((w) => STELLAR_KIT_MINIMAL_WALLET_IDS.has(w.id));
        }
        return list;
    } catch (err) {
        console.warn('[StellarWalletsKit] refreshSupportedWallets failed:', err?.message || err);
        return [];
    }
}

/**
 * Connect to a specific wallet by its kit productId (e.g. FREIGHTER_ID,
 * HANA_ID, XBULL_ID). Triggers the wallet's own approval flow.
 */
export async function connectStellarWalletById(walletId) {
    ensureKitInitialized();
    if (!walletId) throw new Error('No wallet id provided');
    if (!stellarKitUsesFullWalletCatalog() && !STELLAR_KIT_MINIMAL_WALLET_IDS.has(walletId)) {
        throw new Error(
            'This wallet is not enabled in the app. Set REACT_APP_STELLAR_WALLET_KIT_FULL_CATALOG=true to allow it.'
        );
    }
    StellarWalletsKit.setWallet(walletId);
    await alignStellarWalletWithAppNetwork();
    // `getAddress` only reads cached state; `fetchAddress` talks to the module
    // (extension / Albedo / …) and fills the cache — required for custom UIs.
    const { address } = await StellarWalletsKit.fetchAddress();
    if (!address) throw new Error('Wallet did not return an address');
    return {
        address,
        walletId,
        origin: kitWalletIdToAppOrigin(walletId),
    };
}

/**
 * Attempt to restore a previously-selected wallet session without showing UI.
 * Returns `{ address, walletId, origin }` if restore succeeded, else `null`.
 */
export async function restoreStellarWalletsSession() {
    ensureKitInitialized();
    const walletId = StellarWalletsKit.selectedModule?.productId || null;
    if (!walletId) return null;

    try {
        const { address } = await StellarWalletsKit.fetchAddress();
        if (!address) return null;
        await alignStellarWalletWithAppNetwork();
        return {
            address,
            walletId,
            origin: kitWalletIdToAppOrigin(walletId),
        };
    } catch (err) {
        console.warn('[StellarWalletsKit] restore session failed:', err?.message || err);
        return null;
    }
}

/**
 * Sign a Soroban transaction XDR with the currently-selected wallet.
 */
export async function signStellarTransaction(xdr, opts = {}) {
    ensureKitInitialized();
    const networkPassphrase = opts.networkPassphrase || getAppStellarNetworkPassphrase();
    await alignStellarWalletWithAppNetwork(networkPassphrase);

    const mod = activeModule.value;
    if (mod?.productId === HANA_ID && typeof window !== 'undefined' && window.hanaWallet?.stellar?.signTransaction) {
        try {
            const signedTxXdr = await window.hanaWallet.stellar.signTransaction({
                xdr,
                accountToSign: opts.address,
                networkPassphrase,
                ...hanaSigningNetworkFields(networkPassphrase),
            });
            return { signedTxXdr, signerAddress: opts.address };
        } catch (e) {
            throw parseError(e);
        }
    }

    if (mod?.productId === FREIGHTER_ID) {
        try {
            const fo = freighterSigningOpts(networkPassphrase, opts.address);
            const { signedTxXdr, error } = await freighterSignTransaction(xdr, fo);
            if (error) throw parseError(error);
            if (!signedTxXdr) throw new Error('Freighter did not return a signed transaction');
            return { signedTxXdr, signerAddress: opts.address };
        } catch (e) {
            throw parseError(e);
        }
    }

    return StellarWalletsKit.signTransaction(xdr, {
        networkPassphrase,
        address: opts.address,
    });
}

/**
 * Sign a Soroban authorization preimage (base64 XDR of `HashIdPreimage`) for
 * `authorizeEntry` / invoker credentials. Delegates to the active kit module.
 */
export async function signStellarAuthEntry(preimageXdrBase64, opts = {}) {
    ensureKitInitialized();
    const networkPassphrase = opts.networkPassphrase || getAppStellarNetworkPassphrase();
    await alignStellarWalletWithAppNetwork(networkPassphrase);

    const mod = activeModule.value;
    if (mod?.productId === HANA_ID && typeof window !== 'undefined' && window.hanaWallet?.stellar?.signAuthEntry) {
        try {
            const signedAuthEntry = await window.hanaWallet.stellar.signAuthEntry({
                xdr: preimageXdrBase64,
                accountToSign: opts.address,
                networkPassphrase,
                ...hanaSigningNetworkFields(networkPassphrase),
            });
            return { signedAuthEntry, signerAddress: opts.address };
        } catch (e) {
            throw parseError(e);
        }
    }

    if (mod?.productId === FREIGHTER_ID) {
        try {
            const fo = freighterSigningOpts(networkPassphrase, opts.address);
            const { signedAuthEntry, error } = await freighterSignAuthEntry(preimageXdrBase64, fo);
            if (error) throw parseError(error);
            if (signedAuthEntry == null) {
                throw new Error('Freighter did not return signedAuthEntry');
            }
            return { signedAuthEntry, signerAddress: opts.address };
        } catch (e) {
            throw parseError(e);
        }
    }

    return StellarWalletsKit.signAuthEntry(preimageXdrBase64, {
        networkPassphrase,
        address: opts.address,
    });
}

/**
 * Throws if the selected wallet cannot complete Soroban `signAuthEntry`
 * (required when simulation returns unsigned invoker auth entries).
 */
export function assertWalletSupportsSorobanAuthEntrySigning() {
    ensureKitInitialized();
    const id = StellarWalletsKit.selectedModule?.productId;
    if (id && WALLETS_WITHOUT_SIGN_AUTH_ENTRY.has(id)) {
        throw new Error(
            'This action requires Soroban authorization signatures (signAuthEntry). ' +
                'Your wallet does not support that API yet. Please use Freighter or Hana on Stellar Testnet, ' +
                'then try again.'
        );
    }
}

/**
 * Throws if the selected wallet cannot complete SEP-43 `signMessage` (required for
 * homomorphic key derivation on Stellar).
 */
export function assertWalletSupportsStellarSignMessage() {
    ensureKitInitialized();
    const id = StellarWalletsKit.selectedModule?.productId;
    if (id && WALLETS_WITHOUT_SIGN_MESSAGE.has(id)) {
        throw new Error(
            'This app needs SEP-43 message signing (signMessage) to derive encryption keys on Stellar. ' +
                'Rabet, Albedo, Ledger, Trezor, and WalletConnect do not expose that API in the kit. ' +
                'Please connect with Freighter, Hana, xBull, Lobstr, or another wallet that supports signMessage, ' +
                'then try again.'
        );
    }
}

/**
 * Sign an arbitrary message (SEP-43 signMessage) with the currently-selected wallet.
 * Used for deterministic key derivation.
 */
export async function signStellarMessage(message, opts = {}) {
    ensureKitInitialized();
    assertWalletSupportsStellarSignMessage();
    const networkPassphrase = opts.networkPassphrase || getAppStellarNetworkPassphrase();
    await alignStellarWalletWithAppNetwork(networkPassphrase);

    const mod = activeModule.value;
    if (mod?.productId === HANA_ID && typeof window !== 'undefined' && window.hanaWallet?.stellar?.signMessage) {
        try {
            const signedMessage = await window.hanaWallet.stellar.signMessage({
                message,
                accountToSign: opts.address,
                networkPassphrase,
                ...hanaSigningNetworkFields(networkPassphrase),
            });
            return { signedMessage, signerAddress: opts.address };
        } catch (e) {
            throw parseError(e);
        }
    }

    if (mod?.productId === FREIGHTER_ID) {
        try {
            const fo = freighterSigningOpts(networkPassphrase, opts.address);
            const { signedMessage, error } = await freighterSignMessage(message, fo);
            if (error) throw parseError(error);
            if (signedMessage == null) {
                throw new Error('Freighter did not return signedMessage');
            }
            return { signedMessage, signerAddress: opts.address };
        } catch (e) {
            throw parseError(e);
        }
    }

    return StellarWalletsKit.signMessage(message, {
        networkPassphrase,
        address: opts.address,
    });
}

/**
 * Disconnect the currently-selected wallet.
 */
export async function disconnectStellarWallet() {
    ensureKitInitialized();
    try {
        await StellarWalletsKit.disconnect();
    } catch (err) {
        console.warn('[StellarWalletsKit] disconnect failed:', err?.message || err);
    }
}

export { StellarWalletsKit, Networks };
